import { type FC, useEffect, useMemo, useState } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { type CommonAccountReportPropsExtended } from "../../types";
import { FeatureFlags } from "featureFlags";
import {
  useEventLineChartQuery,
  useEventEngagementQuery,
} from "features/Library/Events/EventDetails/Performance/Queries/queries";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import {
  createDateRange,
  dailyActivity,
  lineChartTitles,
  noData,
  type QueryFilter,
  totalActivity,
  validLineData,
  canUseChildAccount,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { type ColumnOption } from "interfaces/columnOptions";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";

import styles from "../../contentOverviewReports.module.scss";
import { ChartWrapper, getDateFormatByCount, HorizontalBarChart, LineChart } from "components/charts";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { barDataHasValues } from "features/Reporting/MsGraph/utils/utils";
import { sharedAccountReportingHorizontalBarProps, sharedAccountReportingLineProps } from "../../shared";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { connect, type ConnectedProps } from "react-redux";
import { bindAction } from "interfaces";
import { reset, setIsReportEnabled } from "features/Reporting/state/toolbar/toolbarSlice";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { doEventExport } from "features/Reporting/state/content/contentActions";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { type AxisDomain } from "d3";
import { MoboLink } from "components/MoboLink/MoboLink";
import { RouteNames } from "enums";
import { processPerformanceParams } from "utils/queryUtils";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";

export type Props = CommonAccountReportPropsExtended &
  PropsFromRedux & {
    includeAccounts?: boolean;
    childAccountId?: number;
    hasCreateAccountsAddOn?: boolean;
    disableLinks?: boolean;
  };

export interface EventsTableEntity {
  EventId: number;
  EventTitle: string;
  Registered: number;
  Attended: number;
  NotAttended: number;
  LastActivity: string; // timestamp (ie "2024-02-05 15:28:13")
}

const fetchTableQuery = async ({ queryKey, signal }: QueryFilter) => {
  const { data } = await axios.get<EventsTableEntity[]>(`/api/reports/v4/overview/events/performance`, {
    params: queryKey[1],
    paramsSerializer: processPerformanceParams,
    signal,
  });
  return data.map((item) => ({
    ...item,
    id: item.EventId,
  }));
};

const colorScale = [reportingColors.blue, reportingColors.greenDark];

const legendLabelFormat = (label: AxisDomain): string => {
  if (label === "Registered") return "Users who registered for an event.";
  if (label === "Attended") return "Users who attended an event.";
  return "Users who registered for an event, but did not attend.";
};

export const EventsReport: FC<Props> = ({
  filter,
  setIsReportEnabled,
  resetIsReportEnabled,
  eventExport,
  setExportAction,
  setHasExportData,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,
  disableLinks,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.EventPerformanceOverview);
  const [chartState, setChartState] = useState(totalActivity);
  const formatDateTime = useReportingFormatWithTimeZone();

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useEventLineChartQuery({ filter, chartPeriod, reportEnabled });
  const engagementQuery = useEventEngagementQuery({ filter, reportEnabled, isOverview: true });
  const tableQuery = useQuery({
    queryKey: ["overviewEventsTable", filter],
    queryFn: fetchTableQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!tableQuery.data && tableQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no events with activity data.",
    });
  }, [tableQuery.data, setHasExportData]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, resetIsReportEnabled, setIsReportEnabled]);

  useEffect(() => {
    setExportAction({
      method: eventExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [eventExport, filter, setExportAction]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.lineData?.Date, lineChartQuery.data?.lineData?.Date],
        yData: [lineChartQuery.data?.lineData?.Registered, lineChartQuery.data?.lineData?.Attended],
      };
    }
    return {
      xData: [lineChartQuery.data?.lineData?.Date, lineChartQuery.data?.lineData?.Date],
      yData: [lineChartQuery.data?.lineData?.CumulativeRegistered, lineChartQuery.data?.lineData?.CumulativeAttended],
    };
  }, [chartState, lineChartQuery.data]);

  const columns: ColumnOption<EventsTableEntity>[] = useMemo(
    () => [
      {
        name: "EVENT",
        width: 7,
        isSortable: false,
        render(item) {
          const isChildAccountContext = canUseChildAccount(childAccountId, hasCreateAccountsAddOn);
          const shouldShowLink =
            (!(filter.showCustomers || !!includeAccounts) || isChildAccountContext) && !disableLinks;
          return (
            <Tooltip
              target={
                <TextTruncate lines={2} className={styles.wordBreak}>
                  {shouldShowLink ? (
                    <MoboLink
                      target={isChildAccountContext ? "_blank" : undefined}
                      accountId={isChildAccountContext ? childAccountId : undefined}
                      to={`/${RouteNames.contentEvents}/${item.EventId}`}
                    >
                      {item.EventTitle || item.EventId.toString()}
                    </MoboLink>
                  ) : (
                    item.EventTitle
                  )}
                </TextTruncate>
              }
              content={item.EventTitle}
            />
          );
        },
      },
      {
        name: "TOTAL REGISTERED",
        width: 4,
        isSortable: false,
        render(item) {
          return (item.Registered ?? 0).toLocaleString();
        },
      },
      {
        name: "TOTAL ATTENDED",
        width: 4,
        isSortable: false,
        render(item) {
          return (item.Attended ?? 0).toLocaleString();
        },
      },
      {
        name: "ATTENDANCE RATE",
        width: 4,
        isSortable: false,
        render(item) {
          const attendanceRate = Math.round((item.Attended / item.Registered) * 100);
          return isFinite(attendanceRate) ? `${attendanceRate}%` : "100%"; // Handles when registered is 0 and attended is non-zero, which can occur if an event was registered for, out of the date range, and then attended within the date range.
        },
      },
      {
        name: "LAST ACTIVITY",
        width: 5,
        isSortable: false,
        render(item) {
          const date = formatDateTime(item.LastActivity);
          return item.LastActivity ? date : "-";
        },
      },
    ],
    [filter.showCustomers, includeAccounts, formatDateTime, childAccountId, hasCreateAccountsAddOn, disableLinks],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={["Registered", "Attended"]}
          colorRange={colorScale}
          onChange={(v) => setChartState(v)}
          legendTooltip={legendLabelFormat}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.lineData.Attended,
              lineChartQuery.data.lineData.CumulativeAttended,
              lineChartQuery.data.lineData.Registered,
              lineChartQuery.data.lineData.CumulativeRegistered,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                {...lineData}
                colorRange={colorScale}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.lineData.Date.length)}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["Total Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && barDataHasValues(engagementQuery.data.engagement) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                data={engagementQuery.data.engagement}
                yAxisTickTooltipFormat={legendLabelFormat}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.performanceCardSection}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={tableQuery.status}>
          {tableQuery.isSuccess && (
            <GenericPerformanceList title="Events" rows={tableQuery.data} columns={columns} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  eventExport: bindActionCreators(doEventExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EventsReport);
export default ConnectedComponent;
